import { Button } from '@/components/ui/button';
import { cn } from '@/utils/helpers';
import { Check, Copy, RotateCcw, ThumbsUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ThumbDown } from './ThumbDown';

interface ChatMessageActionsProps {
  onThumbsUp?: () => Promise<void>;
  onThumbsDown?: (comment?: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
  onCopy?: () => Promise<void>;
  rating?: { score: number };
}

export function ChatMessageActions({
  onThumbsUp,
  onThumbsDown,
  onRefresh,
  onCopy,
  rating,
}: ChatMessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [isThumbsUpLoading, setIsThumbsUpLoading] = useState(false);
  const [isThumbsDownLoading, setIsThumbsDownLoading] = useState(false);
  const [optimisticRating, setOptimisticRating] = useState<number | null>(null);

  const currentRating = useMemo(() => {
    if (optimisticRating !== null) {
      return optimisticRating;
    }
    return rating?.score;
  }, [rating?.score, optimisticRating]);

  const hasThumbsUp = currentRating === 1;
  const hasThumbsDown = currentRating === 0;

  if (!onThumbsUp && !onThumbsDown && !onRefresh && !onCopy) {
    return null;
  }

  const handleThumbsUp = async () => {
    if (!onThumbsUp) return;

    setOptimisticRating(1);
    setIsThumbsUpLoading(true);
    try {
      await onThumbsUp();
    } finally {
      setIsThumbsUpLoading(false);
    }
  };

  const handleThumbsDownClick = () => {
    setOptimisticRating(0);
  };

  const handleSubmitComment = async (comment?: string) => {
    if (!onThumbsDown) return;

    setIsThumbsDownLoading(true);
    try {
      await onThumbsDown(comment);
    } finally {
      setIsThumbsDownLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!onCopy) return;

    await onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-1.5 mt-3">
      {onThumbsUp && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleThumbsUp}
          className="h-7 w-7"
          data-testid="thumbs-up-button"
          disabled={hasThumbsUp || isThumbsUpLoading || isThumbsDownLoading}
        >
          <ThumbsUp
            data-testid={`thumbs-up-icon-${hasThumbsUp ? 'active' : 'inactive'}`}
            className={cn(
              'h-4 w-4',
              (hasThumbsUp || isThumbsUpLoading) && !isThumbsDownLoading
                ? 'text-neutral-700 fill-neutral-700'
                : 'text-neutral-400'
            )}
          />
        </Button>
      )}

      {onThumbsDown && (
        <ThumbDown
          onThumbsDown={handleThumbsDownClick}
          onSubmitComment={handleSubmitComment}
          isActive={hasThumbsDown}
          disabled={isThumbsUpLoading}
          isLoading={isThumbsDownLoading}
        />
      )}

      {onRefresh && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRefresh}
          className="h-7 w-7"
          data-testid="refresh-button"
        >
          <RotateCcw className="h-4 w-4 text-neutral-400" />
        </Button>
      )}

      {onCopy && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCopy}
          className="h-7 w-7"
          data-testid="copy-button"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-dark-0" />
          ) : (
            <Copy className="h-4 w-4 text-neutral-400" />
          )}
        </Button>
      )}
    </div>
  );
}
